import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { AncBulkAction } from './anc-bulk-actions-bar.model';
import { ancDropdownPopperOptions } from '../../utils/anc-dropdown-popper.util';
import { TranslateModule } from '@ngx-translate/core';
import { AncViewportService, isAncViewportBelow } from '../../services/anc-viewport.service';

/** Max action buttons shown inline; additional actions move to the overflow menu. */
const MAX_VISIBLE_ACTIONS = 4;
/**
 * Compact tablets (< 840px, OEM-7023): fewer inline actions so the bar stays on one line;
 * the rest move to the overflow menu. The buttons hold no state, so nothing is lost when
 * the count changes on rotate or resize.
 */
const MAX_VISIBLE_ACTIONS_COMPACT = 2;

/**
 * Pure presentational floating action bar.
 *
 * Consumers supply an `actions` list and handle `(actionClick)` and `(dismiss)` —
 * all business logic stays in the caller.
 *
 * @example
 * <lib-anc-bulk-actions-bar
 *   [count]="selectedIds.length"
 *   itemLabel="Products"
 *   [actions]="bulkActions"
 *   (actionClick)="onBulkAction($event)"
 *   (dismiss)="clearSelection()"
 * ></lib-anc-bulk-actions-bar>
 */
@Component({
    selector: 'lib-anc-bulk-actions-bar',
    standalone: true,
    imports: [CommonModule, TranslateModule, NgbDropdownModule],
    templateUrl: './anc-bulk-actions-bar.component.html',
    styleUrls: ['./anc-bulk-actions-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncBulkActionsBarComponent {
    /** Widens ng-bootstrap's 2px trigger offset to the DS 8px. */
    readonly popperOptions = ancDropdownPopperOptions;

    count      = input<number>(0);
    itemLabel  = input<string>('Items');
    actions    = input<AncBulkAction[]>([]);

    actionClick = output<string>();
    dismiss     = output<void>();

    private readonly viewport = inject(AncViewportService);

    private readonly maxVisible = computed(() =>
        isAncViewportBelow(this.viewport.viewportClass(), 'expanded')
            ? MAX_VISIBLE_ACTIONS_COMPACT
            : MAX_VISIBLE_ACTIONS
    );

    readonly visibleActions = computed(() =>
        this.actions().slice(0, this.maxVisible())
    );

    readonly overflowActions = computed(() => {
        const actions = this.actions();
        const max = this.maxVisible();
        return actions.length > max ? actions.slice(max) : [];
    });

    readonly hasOverflowActions = computed(() => this.overflowActions().length > 0);

    onActionClick(id: string): void {
        this.actionClick.emit(id);
    }

    onDismiss(): void {
        this.dismiss.emit();
    }
}
